import type { Locale } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { CancellationSummaryRow, ReservationPolicyInformation } from '@/models';

export type FormattedTimelineDate = {
  date: string; // e.g. "Jul 3"
  weekday: string; // e.g. "(Tue)"
  time: string; // e.g. "10:46am"
};

// Day-first ("d MMM") vs month-first ("MMM d"), derived from the property's date_format (e.g.
// "DD/MM/YYYY"), so the timeline's date order matches the rest of the reservation UI. Day is
// unpadded to match the design ("Jul 3", not "Jul 03").
const dateToken = (dateFormat?: string): string =>
  dateFormat?.trim().toUpperCase().startsWith('D') ? 'd MMM' : 'MMM d';

/**
 * Format a row's resolved ISO instant for display, in the property's timezone and the guest's
 * locale. Returns null when the row has no resolved dateTime, the timezone is missing/invalid, or
 * the value can't be parsed — the caller renders the row without a date rather than crashing.
 *
 * Display only: the instant is already resolved server-side, so we never recompute it here.
 */
export const formatTimelineDate = (
  dateTime: string | null | undefined,
  timeZone: string | null | undefined,
  locale?: Locale,
  dateFormat?: string,
): FormattedTimelineDate | null => {
  if (!dateTime || !timeZone) {
    return null;
  }
  const instant = new Date(dateTime);
  if (Number.isNaN(instant.getTime())) {
    return null;
  }
  try {
    return {
      date: formatInTimeZone(instant, timeZone, dateToken(dateFormat), {
        locale,
      }),
      weekday: `(${formatInTimeZone(instant, timeZone, 'EEE', { locale })})`,
      time: formatInTimeZone(instant, timeZone, 'h:mmaaa', {
        locale,
      }).toLowerCase(),
    };
  } catch {
    return null;
  }
};

/**
 * True only when the string is a resolvable IANA timezone. A truthy-but-invalid zone (e.g. a
 * corrupted property setting) must fall back to the v1 card rather than render a dateless timeline.
 */
export const isResolvableTimeZone = (
  timeZone: string | null | undefined,
): boolean => {
  if (!timeZone) {
    return false;
  }
  try {
    formatInTimeZone(new Date(0), timeZone, 'yyyy');
    return true;
  } catch {
    return false;
  }
};

/**
 * The timeline is renderable only when at least one row resolved to an absolute instant. When the
 * flag is off (or the endpoint is legacy) rows arrive with null dateTime and the caller falls back
 * to the v1 card. Tolerant of a null/undefined element in the array.
 */
export const hasRenderableTimeline = (
  rows: CancellationSummaryRow[] | null | undefined,
): rows is CancellationSummaryRow[] =>
  Array.isArray(rows) && rows.some((row) => Boolean(row?.dateTime));

/**
 * The amber "free cancellation is no longer available" banner shows once the EARLIEST resolved
 * deadline has passed. We take the minimum resolved instant (order-independent — not relying on the
 * BFF's sort) and compare absolute instants, which is timezone/DST-safe. The caller additionally
 * gates on the policy being refundable, so a fully non-refundable policy never shows the banner.
 */
export const isFreeCancellationPassed = (
  rows: CancellationSummaryRow[] | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!Array.isArray(rows)) {
    return false;
  }
  const earliest = rows
    .map((row) => row?.dateTime)
    .filter((dateTime): dateTime is string => Boolean(dateTime))
    .map((dateTime) => new Date(dateTime).getTime())
    .filter((time) => !Number.isNaN(time))
    .reduce((min, time) => (time < min ? time : min), Number.POSITIVE_INFINITY);

  if (!Number.isFinite(earliest)) {
    return false;
  }
  return now.getTime() >= earliest;
};

/**
 * Whether the V2 cancellation-deadlines timeline should render for this reservation: a smart policy,
 * a resolvable property timezone, and at least one row with a resolved date. Otherwise the caller
 * falls back to the v1 summary card.
 */
export const canRenderCancellationTimeline = (
  policyInformation: ReservationPolicyInformation | null | undefined,
  timeZone: string | null | undefined,
): boolean =>
  policyInformation?.policyTextSource === 'smart' &&
  isResolvableTimeZone(timeZone) &&
  hasRenderableTimeline(policyInformation?.policyText?.cancellationSummaryRows);
